import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { CompanyActivityItem, CompanyAgent } from '../../services/company-api.js';
import { wsClient } from '../../services/websocket-client.js';
import { STRATEGY_STATE_LABELS } from '../../types/agent.js';

const PAGE_SIZE = 15;

@customElement('cp-company-activity-feed')
export class CpCompanyActivityFeed extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;
  @property({ type: Array }) agents: CompanyAgent[] = [];

  @state() private _items: CompanyActivityItem[] = [];
  @state() private _loading = false;
  @state() private _hasMore = false;

  private _wsHandler = (e: Event): void => {
    const detail = (e as CustomEvent).detail;
    if (!detail?.type) return;
    this._handleWsEvent(detail.type, detail.payload);
  };

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
    await this._load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
  }

  private async _load(): Promise<void> {
    if (!this.companyId) return;
    this._loading = true;
    try {
      const items = await companyApi.getCompanyActivity(this.companyId, PAGE_SIZE + 1);
      this._hasMore = items.length > PAGE_SIZE;
      this._items = items.slice(0, PAGE_SIZE);
    } catch {
      // silent
    } finally {
      this._loading = false;
    }
  }

  private async _loadMore(): Promise<void> {
    if (!this.companyId) return;
    const currentCount = this._items.length;
    try {
      const items = await companyApi.getCompanyActivity(this.companyId, currentCount + PAGE_SIZE + 1);
      this._hasMore = items.length > currentCount + PAGE_SIZE;
      this._items = items.slice(0, currentCount + PAGE_SIZE);
    } catch {
      // silent
    }
  }

  private _handleWsEvent(type: string, payload: Record<string, unknown>): void {
    const companyId = (payload?.companyId ?? payload?.company_id) as number | undefined;
    if (companyId !== undefined && companyId !== this.companyId) return;

    let item: CompanyActivityItem | null = null;
    const now = new Date().toISOString();

    switch (type) {
      case 'strategy:state-changed': {
        const agentId = payload.agentId as number ?? payload.agent_id as number ?? null;
        const agentName = this._agentName(agentId);
        const stateTo = payload.state as string ?? payload.stateTo as string ?? payload.state_to as string ?? '?';
        const goalTitle = payload.goalTitle as string ?? payload.goal_title as string ?? null;
        const stateLabel = STRATEGY_STATE_LABELS[stateTo] ?? stateTo;
        item = {
          id: `strategy:live:${Date.now()}`,
          type: 'strategy_change',
          agentId,
          agentName,
          summary: `${agentName ?? 'Agent'}: ${stateLabel}${goalTitle ? ` – ${goalTitle}` : ''}`,
          timestamp: now,
        };
        break;
      }
      case 'agent-scheduling:wakeup': {
        const agentId = payload.agentId as number ?? payload.agent_id as number ?? null;
        const agentName = this._agentName(agentId);
        item = {
          id: `wakeup:live:${Date.now()}`,
          type: 'strategy_change',
          agentId,
          agentName,
          summary: `${agentName ?? 'Agent'} wurde aufgeweckt`,
          timestamp: now,
        };
        break;
      }
      case 'founder-ticket:created': {
        const agentId = payload.agentId as number ?? payload.agent_id as number ?? null;
        const agentName = this._agentName(agentId);
        const title = payload.title as string ?? 'Neues Ticket';
        item = {
          id: `ticket:${payload.ticketId ?? 'live'}:created`,
          type: 'ticket_created',
          agentId,
          agentName,
          summary: `${agentName ?? 'Agent'}: ${title}`,
          timestamp: now,
        };
        break;
      }
      case 'founder-ticket:updated': {
        const agentId = payload.agentId as number ?? payload.agent_id as number ?? null;
        const agentName = this._agentName(agentId);
        const title = payload.title as string ?? '';
        item = {
          id: `ticket:${payload.ticketId ?? 'live'}:responded`,
          type: 'ticket_responded',
          agentId,
          agentName,
          summary: `Ticket beantwortet${title ? `: ${title}` : ''}`,
          timestamp: now,
        };
        break;
      }
      case 'hire:confirmed':
      case 'founder-board:hire-request-approved': {
        const templateName = payload.template_name as string ?? payload.templateName as string ?? '';
        item = {
          id: `hire:${payload.hireRequestId ?? 'live'}:confirmed`,
          type: 'hire_confirmed',
          agentId: null,
          agentName: null,
          summary: `Einstellung genehmigt${templateName ? `: ${templateName}` : ''}`,
          timestamp: now,
        };
        break;
      }
      case 'founder-board:hire-request-created': {
        const templateName = payload.templateName as string ?? '';
        item = {
          id: `hire:${payload.hireRequestId ?? 'live'}:requested`,
          type: 'hire_request',
          agentId: null,
          agentName: null,
          summary: `Einstellungsantrag: ${templateName}`,
          timestamp: now,
        };
        break;
      }
    }

    if (item) {
      // Deduplizieren: gleiches Prefix (z.B. ticket:5) wird nicht doppelt angezeigt
      this._items = [item, ...this._items.filter((i) => i.id !== item!.id)];
    }
  }

  private _agentName(agentId: number | null): string | null {
    if (agentId === null) return null;
    return this.agents.find((a) => a.id === agentId)?.name ?? null;
  }

  private _icon(type: CompanyActivityItem['type']): string {
    switch (type) {
      case 'strategy_change': return '◎';
      case 'ticket_created': return '✦';
      case 'ticket_responded': return '↩';
      case 'hire_request': return '⊕';
      case 'hire_confirmed': return '✓';
      default: return '·';
    }
  }

  private _relativeTime(ts: string): string {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'gerade eben';
    if (mins < 60) return `vor ${mins} Min.`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    return `vor ${days} T.`;
  }

  override render() {
    if (this._loading && this._items.length === 0) {
      return html`<p class="cp-activity__loading">Laden...</p>`;
    }

    if (this._items.length === 0) {
      return html`<p class="cp-activity__empty">Noch keine Aktivitaet</p>`;
    }

    return html`
      <div class="cp-activity__timeline">
        ${this._items.map((item) => html`
          <div class="cp-activity__item">
            <span class="cp-activity__icon cp-activity__icon--${item.type}">${this._icon(item.type)}</span>
            <div class="cp-activity__content">
              <p class="cp-activity__summary">${item.summary}</p>
              <span class="cp-activity__time">${this._relativeTime(item.timestamp)}</span>
            </div>
          </div>
        `)}
      </div>
      ${this._hasMore ? html`
        <button class="cp-activity__more-btn" @click=${this._loadMore}>
          Mehr laden
        </button>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-activity-feed': CpCompanyActivityFeed;
  }
}
